import { useEffect, useState } from "react";
import { FiCheck, FiX } from "react-icons/fi";
import type { Prompt } from "../../../types/PromptTypes";
import { promptCoverSrc } from "../../../utils/smileDesign/promptCoverSrc";
import { formatStlFileSize } from "../../../utils/smileDesign/extractStlJsonInBrowser";

interface Props {
    prompt: Prompt;
    editName: string;
    editHeading: string;
    editDescription: string;
    editActive: boolean;
    editStlFile: File | null;
    editCoverFile?: File | null;
    submitting?: boolean;

    setEditName: (v: string) => void;
    setEditHeading: (v: string) => void;
    setEditDescription: (v: string) => void;
    setEditActive: (v: boolean) => void;
    setEditStlFile: (v: File | null) => void;
    setEditCoverFile?: (v: File | null) => void;

    autoResizeTextarea: (
        e: React.ChangeEvent<HTMLTextAreaElement>
    ) => void;

    onSave: () => void;
    onCancel: () => void;
}

export default function EditPromptModal({
    prompt,
    editName,
    editHeading,
    setEditHeading,
    editDescription,
    editActive,
    editStlFile,
    editCoverFile = null,
    submitting = false,
    setEditName,
    setEditDescription,
    setEditActive,
    setEditStlFile,
    setEditCoverFile,
    autoResizeTextarea,
    onSave,
    onCancel,
}: Props) {
    const [newCoverUrl, setNewCoverUrl] = useState<string | null>(null);

    useEffect(() => {
        if (!editCoverFile) {
            setNewCoverUrl(null);
            return;
        }
        const url = URL.createObjectURL(editCoverFile);
        setNewCoverUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [editCoverFile]);

    const storedCover = promptCoverSrc(prompt.filter_image || prompt.front_thumb);
    const currentCover = newCoverUrl || storedCover || null;

    return (
        <div className="space-y-3">
            <div className="flex items-start justify-between">
                <div>
                    <h2 className="text-lg font-bold text-white">Edit Filter</h2>
                    <p className="text-xs text-[#8b8f8d] mt-1">
                        Replace the STL (stored on Box), cover image, heading, or optional description.
                    </p>
                </div>
                <span
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${
                        editActive
                            ? "bg-green-500/10 border-green-500/20 text-green-400"
                            : "bg-red-500/10 border-red-500/20 text-red-400"
                    }`}
                >
                    {editActive ? "ACTIVE" : "INACTIVE"}
                </span>
            </div>

            <div>
                <label className="block text-sm font-medium text-white mb-2">
                    Heading
                </label>
                <input
                    value={editHeading}
                    onChange={(e) => setEditHeading(e.target.value)}
                    placeholder="Enter heading..."
                    className="w-full bg-[#161917] border border-[#2a2e2c] rounded-xl px-3 py-2.5 text-sm text-white placeholder-[#5b605e] focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-white mb-2">
                    Filter Name
                </label>
                <input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="Enter filter name..."
                    className="w-full bg-[#161917] border border-[#2a2e2c] rounded-xl px-3 py-2.5 text-sm text-white placeholder-[#5b605e] focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-white mb-2">
                    Description <span className="text-[#5b605e]">(optional)</span>
                </label>
                <textarea
                    rows={3}
                    value={editDescription}
                    onChange={(e) => {
                        setEditDescription(e.target.value);
                        autoResizeTextarea(e);
                    }}
                    placeholder="Optional notes for this filter…"
                    className="w-full bg-[#161917] border border-[#2a2e2c] rounded-xl px-3 py-2.5 text-sm text-white placeholder-[#5b605e] focus:outline-none focus:ring-2 focus:ring-cyan-500/30 resize-y min-h-[100px] max-h-[240px]"
                />
            </div>

            <div className="bg-[#161917] border border-[#2a2e2c] rounded-xl p-3">
                <div className="flex items-center justify-between">
                    <div>
                        <h4 className="text-white text-sm font-medium">Filter Status</h4>
                        <p className="text-xs text-[#8b8f8d] mt-1">
                            Control whether this filter is available.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setEditActive(!editActive)}
                        className={`relative h-7 w-12 rounded-full transition-all duration-300 ${
                            editActive ? "bg-green-500" : "bg-[#333735]"
                        }`}
                    >
                        <span
                            className={`absolute top-0.5 h-6 w-6 rounded-full bg-white transition-all duration-300 ${
                                editActive ? "left-5.5" : "left-0.5"
                            }`}
                        />
                    </button>
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-white mb-2">
                    Original STL
                </label>
                <label className="flex flex-col items-center justify-center gap-1 w-full bg-[#161917] border border-dashed border-[#2a2e2c] rounded-xl px-3 py-4 cursor-pointer">
                    <input
                        type="file"
                        accept=".stl,model/stl,application/sla"
                        className="hidden"
                        onChange={(e) => setEditStlFile(e.target.files?.[0] || null)}
                    />
                    <span className="text-sm text-white">
                        {editStlFile
                            ? `${editStlFile.name} · ${formatStlFileSize(editStlFile.size)}`
                            : prompt.stl_original_name || "Upload STL to replace"}
                    </span>
                    <span className="text-xs text-[#8b8f8d] text-center px-2">
                        Replacing uploads a new original STL to Box for generation.
                    </span>
                </label>
            </div>

            <div>
                <label className="block text-sm font-medium text-white mb-2">
                    Cover Image
                </label>
                <label className="flex flex-col items-center justify-center gap-1 w-full bg-[#161917] border border-dashed border-[#2a2e2c] rounded-xl px-3 py-4 cursor-pointer">
                    <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/avif"
                        className="hidden"
                        onChange={(e) => setEditCoverFile?.(e.target.files?.[0] || null)}
                    />
                    <span className="text-sm text-white">
                        {editCoverFile
                            ? editCoverFile.name
                            : currentCover
                                ? "Replace cover image"
                                : "Upload cover image"}
                    </span>
                </label>
                {currentCover && (
                    <img
                        src={currentCover}
                        alt="Cover"
                        className="mt-2 w-full max-h-40 object-contain rounded-xl border border-[#2a2e2c] bg-[#C1C1C3]"
                    />
                )}
                {editCoverFile && (
                    <button
                        type="button"
                        onClick={() => setEditCoverFile?.(null)}
                        className="mt-2 text-xs text-red-400 hover:text-red-300"
                    >
                        Cancel new cover
                    </button>
                )}
            </div>

            {submitting && (
                <div className="bg-cyan-500/10 border border-cyan-500/20 text-cyan-200 rounded-xl p-3 text-sm">
                    {editStlFile ? "Uploading STL to Box…" : "Saving filter…"}
                </div>
            )}

            <div className="flex gap-3 pt-1">
                <button
                    onClick={onCancel}
                    disabled={submitting}
                    className="flex-1 bg-[#1a1d1c] border border-[#2a2e2c] hover:bg-[#232625] text-white py-2.5 rounded-xl flex items-center justify-center gap-2 font-medium disabled:opacity-50"
                >
                    <FiX />
                    Cancel
                </button>
                <button
                    onClick={onSave}
                    disabled={submitting}
                    className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-black py-2.5 rounded-xl flex items-center justify-center gap-2 font-semibold disabled:opacity-50"
                >
                    <FiCheck />
                    {submitting ? "Saving…" : "Save Changes"}
                </button>
            </div>
        </div>
    );
}
